import { ConversationStreamEvent } from '@centecomm/portal-service';
import React from 'react';
import { useSelector } from 'react-redux';
import { useDateFnsLocale } from 'Core/i18n/Hooks/useTranslationExtensions';
import { conversationsSelectors } from 'State';
import { chunkConversationEvents } from 'utils';
import { ChunkedEvents } from '../Types';
import ConversationChunkItems from './ConversationChunkItems';
import styles from './conversation-event-list.module.css';

interface ConversationEventListProps {
    conversationId: number;
    hasDisclaimer: boolean;
}

const filterEvents = (eventsToFilter: ConversationStreamEvent[]): ConversationStreamEvent[] =>
    eventsToFilter.filter(
        ({ type }: ConversationStreamEvent) => !(type === 'ConversationCreated' || type === 'ConversationMessageRead'),
    );

function ConversationEventList({
    conversationId,
    hasDisclaimer,
}: ConversationEventListProps): React.ReactElement<ConversationEventListProps> {
    const dateFnsLocale = useDateFnsLocale();
    const events: ConversationStreamEvent[] = useSelector(conversationsSelectors.eventsForConversation(conversationId));

    const [eventChunks, setEventChunks] = React.useState<ChunkedEvents>(
        chunkConversationEvents(filterEvents(events), dateFnsLocale),
    );

    const scrollNode = React.useRef<HTMLLIElement>(null);

    React.useEffect(() => {
        if (scrollNode.current) {
            scrollNode.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [events.length]);

    React.useEffect(() => {
        setEventChunks(chunkConversationEvents(filterEvents(events), dateFnsLocale));
    }, [events.length]);

    return (
        <ul
            className={styles.ConversationEventList}
            style={{ height: hasDisclaimer ? 'calc(100% - 8.5rem)' : 'calc(100% - 6.5rem)' }}
        >
            {Object.keys(eventChunks).map((eventDate: string) => (
                <React.Fragment key={`event-date-${eventDate}`}>
                    <li className={styles.EventDate}>
                        <p>{eventDate}</p>
                    </li>
                    <ConversationChunkItems chunks={eventChunks[eventDate]} conversationId={conversationId} />
                </React.Fragment>
            ))}
            <li className={`${styles.Scroll}`} ref={scrollNode} />
        </ul>
    );
}

export default ConversationEventList;
